import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { useGoalsDB } from '@/hooks/useGoalsDB';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { reportError } from '@/lib/error-reporting';
import { Loader2 } from 'lucide-react';
import {
  AnalysisSkeleton,
  ErrorAlert,
  SkillGapResults,
  GoalSuggestionsResults,
  ProgressCoachingResults,
  CareerPathResults,
} from './results';
import type {
  SkillGapAnalysis,
  GoalSuggestions,
  ProgressCoaching,
  CareerPathDiscovery,
} from './results/types';
import { getETypeColor, getPriorityColor } from '@/lib/goal-colors';

export type AnalysisType = 'skill_gap' | 'goal_suggestions' | 'progress_coaching' | 'career_path_discovery';

interface CoachConversationProps {
  analysisType: AnalysisType | null;
  onComplete?: () => void;
  onAddGoal?: (title: string, description: string, eType: string) => void;
  onAddHabit?: (goalId: string, title: string) => void;
  onSelectCareerPath?: (title: string) => void;
  onSetTargetRoleManually?: () => void;
}

export const CoachConversation = ({
  analysisType,
  onComplete,
  onAddGoal,
  onAddHabit,
  onSelectCareerPath,
  onSetTargetRoleManually,
}: CoachConversationProps) => {
  const { user } = useAuth();
  const { profile } = useProfile();
  const { goals } = useGoalsDB();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ type: string; data: SkillGapAnalysis | GoalSuggestions | ProgressCoaching | CareerPathDiscovery } | null>(null);

  // Local UI state for result components
  const [expandedSkills, setExpandedSkills] = useState<Set<number>>(new Set());
  const [addedGoalTitles, setAddedGoalTitles] = useState<Set<string>>(new Set());

  const toggleSkillExpanded = (i: number) => {
    setExpandedSkills(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const handleAddGoal = (title: string, description: string, eType: string) => {
    onAddGoal?.(title, description, eType);
    setAddedGoalTitles(prev => new Set(prev).add(title));
  };

  const handleRemoveGoalTitle = (title: string) => {
    setAddedGoalTitles(prev => {
      const next = new Set(prev);
      next.delete(title);
      return next;
    });
  };

  const runAnalysis = async (type: AnalysisType) => {
    if (!user) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setExpandedSkills(new Set());
    setAddedGoalTitles(new Set());

    try {
      const coreValuesResp = await supabase
        .from('core_values')
        .select('value_name, description')
        .eq('user_id', user.id);

      const body: Record<string, unknown> = {
        type,
        jobRole: profile?.job_role,
        targetJobRole: profile?.target_job_role,
        skills: profile?.skills || [],
        goals: goals.map(g => ({ title: g.title, progress: g.progress, e_types: g.eTypes })),
        coreValues: coreValuesResp.data?.map(v => ({ name: v.value_name, description: v.description })) || [],
      };

      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/career-coach`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify(body),
        },
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        if (response.status === 429 && errData.retry_after_seconds) {
          const sec = errData.retry_after_seconds;
          const timeMsg = sec >= 60 ? `${Math.ceil(sec / 60)} minute(s)` : `${sec} seconds`;
          throw new Error(`Too many requests. Please try again in ${timeMsg}.`);
        }
        throw new Error(errData.error || `Request failed (${response.status})`);
      }

      const data = await response.json();
      setResult({ type: data.type, data: data.data });
      onComplete?.();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Something went wrong';
      setError(msg);
      reportError(err, 'ai-coach', { action: `panel-${type}` });
    } finally {
      setLoading(false);
    }
  };

  // Track last analysis type to avoid re-running
  const lastTypeRef = useRef<string | null>(null);

  // Auto-run when analysisType changes (includes user so we retry once auth resolves)
  useEffect(() => {
    if (analysisType && user && analysisType !== lastTypeRef.current) {
      lastTypeRef.current = analysisType;
      runAnalysis(analysisType);
    }
  }, [analysisType, user]);

  if (!analysisType) {
    return (
      <div className="px-4 py-6 text-center text-sm text-muted-foreground">
        Choose a quick start above to begin
      </div>
    );
  }

  if (loading) {
    return (
      <div className="px-4 py-4">
        <div className="flex items-center gap-2 mb-3">
          <Loader2 className="w-4 h-4 animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">Analyzing...</span>
        </div>
        <AnalysisSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 py-4">
        <ErrorAlert message={error} onRetry={() => runAnalysis(analysisType)} />
      </div>
    );
  }

  if (result) {
    const goalsForPicker = goals.map(g => ({ id: g.id, title: g.title }));

    return (
      <div className="px-4 py-4">
        {result.type === 'skill_gap' && (
          <SkillGapResults
            analysis={result.data as SkillGapAnalysis}
            expandedSkills={expandedSkills}
            toggleSkillExpanded={toggleSkillExpanded}
            getPriorityColor={getPriorityColor}
            getETypeColor={getETypeColor}
          />
        )}
        {result.type === 'goal_suggestions' && (
          <GoalSuggestionsResults
            suggestions={result.data as GoalSuggestions}
            onAddGoal={onAddGoal ? handleAddGoal : undefined}
            onRemoveGoal={handleRemoveGoalTitle}
            addedGoalTitles={addedGoalTitles}
            getETypeColor={getETypeColor}
            getPriorityColor={getPriorityColor}
          />
        )}
        {result.type === 'progress_coaching' && (
          <ProgressCoachingResults
            coaching={result.data as ProgressCoaching}
            goals={goalsForPicker}
            addedGoalTitles={addedGoalTitles}
            onAddFocusAreaAsGoal={onAddGoal ? (title, desc) => handleAddGoal(title, desc, 'education') : undefined}
            onAddFocusAreaAsHabit={onAddHabit}
          />
        )}
        {result.type === 'career_path_discovery' && onSelectCareerPath && (
          <CareerPathResults
            discovery={result.data as CareerPathDiscovery}
            onSelectPath={onSelectCareerPath}
            onSetManually={onSetTargetRoleManually}
          />
        )}
      </div>
    );
  }

  return null;
};
